/**
 * Sessão do usuário no front.
 *
 * A verdade mora no cookie HttpOnly, que o JavaScript não lê — de propósito,
 * é o que impede um XSS de roubar a sessão. O que este contexto guarda é só o
 * retrato de quem está logado, buscado em `/api/auth/me` e revalidado pelo
 * TanStack Query. Nenhuma decisão de permissão depende disso: o servidor
 * verifica de novo em toda rota.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface SessionUser {
  id: string;
  displayName: string;
  email: string | null;
  role: 'admin' | 'staff' | 'captain' | 'player';
  avatarUrl: string | null;
}

export interface OwnedTeam {
  id: string;
  slug: string;
  name: string;
  shortName: string;
  /** Imagem ou escudo padrão do time (ver `@mpc/core`, escudo.ts). */
  crestUrl?: string | null;
}

interface AuthValue {
  user: SessionUser | null;
  teams: OwnedTeam[];
  loading: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isCaptain: boolean;
  refresh: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

const meQuery = {
  queryKey: ['auth', 'me'] as const,
  queryFn: () => api<{ user: SessionUser | null; teams: OwnedTeam[] }>('/auth/me'),
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    ...meQuery,
    // A sessão dura 30 dias; revalidar a cada foco de janela seria desperdício.
    staleTime: 5 * 60_000,
    retry: false,
  });

  const logout = useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      // Limpa tudo, não só a sessão: dados do capitão não podem sobrar em
      // cache para o próximo que usar o mesmo navegador.
      queryClient.clear();
    },
  });

  const user = data?.user ?? null;
  const ownedTeams = data?.teams ?? [];

  return (
    <AuthContext.Provider
      value={{
        user,
        teams: ownedTeams,
        loading: isLoading,
        isAdmin: user?.role === 'admin',
        isStaff: user?.role === 'admin' || user?.role === 'staff',
        isCaptain: user?.role === 'captain' || ownedTeams.length > 0,
        refresh: () => void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
        logout: () => logout.mutate(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return value;
}

/* ------------------------------ chamadas ------------------------------ */

export const authApi = {
  /**
   * Cadastro de equipe: cria a conta e o time no mesmo passo.
   *
   * É a porta de entrada de quem chega de fora. `register` continua existindo
   * para a organização abrir conta sem time, e o servidor só aceita de admin.
   */
  registrarTime: (body: {
    email: string;
    password: string;
    name: string;
    shortName: string;
    platform: string;
    /* Opcional: quem vincula o clube da EA é a organização, pelo painel. */
    eaClubId?: string;
    /** Escudo padrão escolhido, no código de `codificarEscudo`. */
    crest?: string;
  }) =>
    api<{ user: SessionUser; team: { slug: string; id: string } }>('/auth/registrar-time', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  register: (body: { email: string; password: string; displayName: string }) =>
    api<{ user: SessionUser }>('/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body: { email: string; password: string }) =>
    api<{ user: SessionUser }>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  /** Avisa a organização de que alguém não consegue entrar. */
  /** `envio` diz por onde o link chega: e-mail direto, ou a organização. */
  esqueciSenha: (email: string) =>
    api<{ ok: true; envio?: 'email' | 'organizacao' }>('/auth/senha/esqueci', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  conferirLinkDeSenha: (token: string) =>
    api<{ email: string; displayName: string | null; expiraEm: number }>(
      `/auth/senha/redefinir/${token}`,
    ),
  redefinirSenha: (token: string, password: string) =>
    api<{ ok: true; email: string }>(`/auth/senha/redefinir/${token}`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  discordUrl: (returnTo: string) =>
    `/api/auth/discord?returnTo=${encodeURIComponent(returnTo)}`,
};
